<template>
	<div class="results-toolbar__filter-bar">
		<div class="results-toolbar__filter">
			<div class="results-toolbar__search">
				<Search
					variant="toggle-icons"
					:placeholder="'Search by role name'"
					:timeout="300"
					v-model="filters.search"
					@change="emitValues"
				/>
			</div>

			<div class="results-toolbar__filters-container">
				<Button
					v-if="showResetFilters"
					class="results-toolbar__restore-filters"
					className="btn--link"
					labelText="Restore Filters"
					:btnClick="resetFilters"
				/>
				<div class="results-toolbar__filter-desktop" ref="desktopRef">
					<DropdownV2
						name="status"
						:showClearSelectedButton="true"
						v-model="filters.active"
						:options="statusOptions"
						dropdownWidth="165px"
						@change="emitValues"
					/>
					<DropdownV2
						name="roleTypes"
						:showClearSelectedButton="true"
						v-model="filters.clientId"
						:options="roleTypesOptions"
						dropdownWidth="165px"
						@change="emitValues"
					/>
				</div>
				<div class="results-toolbar__filter-tablet" ref="mobileRef">
					<Button
						className="btn--primary"
						:labelText="roleFilterLabel"
						iconClassName="icon-filter"
						:iconBeforeLabel="true"
						:btnClick="() => (displayFiltersModal = true)"
					/>
				</div>
				<Modal
					v-if="displayFiltersModal"
					:display="true"
					title="Filters"
					@show="displayFiltersModal = false"
					ref="modalFilterRef"
				>
					<template #modal-content>
						<div class="results-toolbar__filter-modal">
							<DropdownV2
								name="status"
								:showClearSelectedButton="true"
								v-model="filters.active"
								:options="statusOptions"
								dropdownWidth="300px"
							/>
							<DropdownV2
								name="roleTypes"
								:showClearSelectedButton="true"
								v-model="filters.clientId"
								:options="roleTypesOptions"
								dropdownWidth="300px"
							/>
						</div>
					</template>
					<template #modal-footer>
						<ButtonGroup>
							<template #container>
								<Button
									labelText="Apply"
									className="btn--primary"
									:btnClick="applyFilterUsingModal"
									:disabled="!filtersModalHaveChanges"
								/>
								<Button
									labelText="Cancel"
									className="btn--link"
									:btnClick="closeModalFilters"
								/>
							</template>
						</ButtonGroup>
					</template>
				</Modal>
			</div>
		</div>
	</div>
</template>

<script>
import _ from 'lodash';
import Search from '@/components/forms/search/search';
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import Modal from '@/components/shells/modal/modals';
import ButtonGroup from '@/components/forms/button/button-group';
import filtersFieldsCustomMixin from '@/mixins/filters-fields-custom-mixin';
import { deepEqualNoFn } from '@/utils/object';
import { countsDiffObjects } from '@/utils/function';

export default {
	name: 'RolesFilters',
	components: {
		Search,
		DropdownV2,
		Button,
		Modal,
		ButtonGroup
	},
	model: {
		prop: 'value'
	},
	props: {
		value: {
			type: Object,
			default: () => {}
		},
		className: {
			type: String
		},
		defaultFilters: Object
	},
	mixins: [filtersFieldsCustomMixin],
	data() {
		return {
			displayFiltersModal: false,
			filters: {},
			currentDefaultFilters: {
				...this.value
			},
			statusOptions: [
				{
					text: 'All Status',
					value: null
				},
				{
					text: 'Active',
					value: 'true'
				},
				{
					text: 'Inactive',
					value: 'false'
				}
			],
			roleTypesOptions: [
				{
					text: 'All Role Types',
					value: null
				},
				{
					text: 'Consultant Role',
					value: 'vue-support-external'
				},
				{
					text: 'Internal Role',
					value: 'vue-support-portal'
				}
			],
			keysToPick: ['active', 'clientId']
		};
	},
	created() {
		this.updateLocalValues(this.value);
	},
	computed: {
		filtersModalHaveChanges() {
			return !deepEqualNoFn(this.filters, this.value, true);
		},
		totalFiltersNumber() {
			return countsDiffObjects(
				{ ...this.defaultFilters },
				{ ...this.filters },
				['search']
			);
		},
		roleFilterLabel() {
			if (this.totalFiltersNumber) {
				return ` Filters (${this.totalFiltersNumber})`;
			}
			return ' Filters';
		},
		showResetFilters() {
			return !deepEqualNoFn(
				this.filteredFilters,
				this.filteredCurrentDefaultFilters,
				true
			);
		},
		filteredFilters() {
			return _.pick(this.filters, this.keysToPick);
		},
		filteredCurrentDefaultFilters() {
			return _.pick(this.defaultFilters, this.keysToPick);
		}
	},
	methods: {
		updateLocalValues(obj) {
			if (!deepEqualNoFn(this.filters, obj, true)) {
				this.filters = _.cloneDeep(obj);
			}
		},
		applyFilterUsingModal() {
			this.displayFiltersModal = false;
			this.$emit('input', { ...this.filters });
		},
		resetFilters() {
			const resetFilters = Object.assign(
				{},
				this.filters,
				this.filteredCurrentDefaultFilters
			);
			this.updateLocalValues(resetFilters);
			this.currentDefaultFilters = _.cloneDeep(this.filters);

			this.$emit('input', { ...this.filters });
		},
		closeModalFilters() {
			this.filters = _.cloneDeep(this.value);
			this.displayFiltersModal = false;
		},
		emitValues() {
			if (!deepEqualNoFn(this.filters, this.value, true)) {
				this.$emit('input', { ...this.filters });
			}
		},
		getModelValues() {
			const status = this.statusOptions.find(
				element => element.value === this.value.status
			);

			const client = this.roleTypesOptions.find(
				element => element.value === this.value.clientId
			);

			const obj = {};
			if (status) {
				obj.filterValue1 = status.text;
			}
			if (client) {
				obj.filterValue2 = client.text;
			}

			return obj;
		}
	},
	watch: {
		value: {
			handler(newValue) {
				this.updateLocalValues(newValue);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.results-toolbar {
	&__filter-bar {
		display: inline;
		width: 100%;
	}

	&__filters-container {
		display: flex;
	}

	&__search {
		display: flex;
	}

	&__filter {
		.dropdown {
			margin: 0;
		}
	}
	&__restore-filters {
		margin-right: $gutter-lg;
	}
	&__filter-modal {
		display: grid;
		gap: 12px;
	}
	&__filter-tablet {
		@include mq($from: lg) {
			display: none;
		}
	}
	&__filter-desktop {
		display: none;

		@include mq($from: lg) {
			display: flex;
			gap: $gutter-lg;
		}
	}
}
@media (min-width: 48em) {
	.results-toolbar.results-toolbar--variant-three .results-toolbar__filter {
		justify-content: space-between;
		display: flex;
	}
}
</style>
